export function onTransitionEnd(
  el: Element,
  callback: (e: Event) => void,
): () => void {
  const onTransitionEnd = (event: Event) => {
    if (event.target !== el) {
      return;
    }
    callback(event);
    el.removeEventListener('transitionend', onTransitionEnd);
  };

  el.addEventListener('transitionend', onTransitionEnd);
  return () => el.removeEventListener('transitionend', onTransitionEnd);
}

export function isEmptyValue(value: string): boolean {
  return !value || value === '0' || value === '0px';
}

function setHeightAndVisibility(element: HTMLElement, height: string) {
  element.style.height = height;
  element.style.visibility = isEmptyValue(height) ? 'hidden' : 'visible';
}

export function transitionHeightAuto(
  element: HTMLElement,
  isOpen: boolean,
  closedHeight: string,
): void | (() => void) {
  if (!element) {
    return;
  }

  const currentHeight = element.style.height;
  // On the first render, set the height directly.
  if (!currentHeight) {
    // if the element doesn't have a fixed height, use the closedHeight if closed
    // auto if open
    setHeightAndVisibility(element, isOpen ? 'auto' : closedHeight);
    return;
  }

  // if height has already been set return early
  if (
    (isOpen && currentHeight === 'auto') ||
    (!isOpen && currentHeight === closedHeight)
  ) {
    return;
  }

  // the parent's height is set to 0 when closed, so measure the child height
  const child = element.firstElementChild;

  if (!child) {
    return;
  }

  const styles = window.getComputedStyle(element);
  const transitionsDisabled = styles.getPropertyValue('transition') === 'none';

  const openHeight = `${child.getBoundingClientRect().height}px`;

  // When animating from auto to a specific height, seamlessly swap 'auto' for
  // the current height as a fixed value (e.g. '403px').
  if (currentHeight === 'auto' && !transitionsDisabled) {
    element.style.height = openHeight;
    // Force the element to paint by querying offsetHeight. This ensures that
    // the two updates to element.style.height won’t get batched into the same
    // browser paint and immediately jump to the end state.
    // eslint-disable-next-line no-unused-expressions
    element.offsetHeight;
  } else if (element.style.visibility === 'hidden' && isOpen) {
    element.style.visibility = 'visible';
  }

  const end = () => {
    if (isOpen) {
      element.style.height = 'auto';
    } else if (isEmptyValue(closedHeight)) {
      element.style.visibility = 'hidden';
    }
  };

  element.style.height = isOpen ? openHeight : closedHeight;

  if (transitionsDisabled) {
    end();
  } else {
    // after the transition is over, set the height back to auto if open
    return onTransitionEnd(element, end);
  }
}

// lifecycle notes
//
// rendering the element the first time
// - set the height to auto or minHeight & set visibility

// showing a hidden element
// - measure the child
// - set height to fixed child height
// - when transition is over, set height back to auto

// hiding a visible element
// - currentHeight is set to `auto` when entering the function
// - set the height back to a fixed height so you can transition to 0
// - then set height to 0
